import * as d3 from "d3";
import { PlannerGlobals } from "src/components/Planner/planner/planner-globals";
import { getRoomById, isRoomOutside, uDev, wallsOfRoom } from "src/components/Planner/planner/uDev";
import {
  arrayIntersection,
  CMToPx,
  countIndexesIncl,
  getWallById,
  line_line_intersect,
  line_line_intersect2,
  radToDeg,
  segmentClosestPointToPoint,
  segmentClosestPointToPoint_S,
  segPt,
  uMath,
  vectorRotationDegree
} from "src/components/Planner/planner/uMath";

export const MEASURE_LINE_MIN_PXLEN = 10;

export const activeClassName = 'active-d3-item';

export function redrawEntitySelectRect() {
  d3.selectAll('.entitySelectRect').remove();
  var { x1, y1, width, height } = getEntitySelectRect();

  PlannerGlobals.container.append('rect')
  .attr('class', 'entitySelectRect')
  .attr('x', x1)
  .attr('y', y1)
  .attr('width', width)
  .attr('height', height)
  .attr('fill', 'rgba(0, 220, 165, 0.15)')
  .attr('stroke', 'rgba(0, 183, 137, 0.9)')
  .attr('stroke-width', '0.2px')
  .attr('rx', '0.2px')
  .attr('ry', '0.2px');
}

export function getEntitySelectRect() {
  var x1 = PlannerGlobals.entitySelectStartPos[0] < PlannerGlobals.entitySelectEndPos[0] ? PlannerGlobals.entitySelectStartPos[0] : PlannerGlobals.entitySelectEndPos[0];
  var y1 = PlannerGlobals.entitySelectStartPos[1] < PlannerGlobals.entitySelectEndPos[1] ? PlannerGlobals.entitySelectStartPos[1] : PlannerGlobals.entitySelectEndPos[1];
  var width = Math.abs(PlannerGlobals.entitySelectEndPos[0] - PlannerGlobals.entitySelectStartPos[0]);
  var height = Math.abs(PlannerGlobals.entitySelectEndPos[1] - PlannerGlobals.entitySelectStartPos[1]);
  var x2 = x1 + width;
  var y2 = y1 + height;
  return { x1, y1, width, height, x2, y2 };
}

/**
 *
 * @param {Point} startPt
 * @param {Point} endPt
 * @param {number} fromRoomId
 */
export function getNeighborWallMod(startPt, endPt, fromRoomId) {
  // props of original room
  var srcAngle = vectorRotationDegree(startPt, endPt);
  var srcSeg = uMath.segmentFromPoints(startPt, endPt);

  // all the walls of other rooms
  var candidates = wallModHelper.getAllStraightPaths(PlannerGlobals.rooms.filter(function (room) { return room.id != fromRoomId; }));
  var results = [];

  // pre checks
  candidates.forEach(function (sp) {
    // props of the other wall
    var tarPath = uMath.segmentFromPoints(sp.points[0], uMath.array.last(sp.points));
    // point-line distance of source wall to target wall
    var tarStart = segmentClosestPointToPoint_S(startPt, tarPath);
    var tarEnd = segmentClosestPointToPoint_S(endPt, tarPath);
    var dist1 = uMath.pointDistance(startPt, tarStart);
    var dist2 = uMath.pointDistance(endPt, tarEnd);
    // if both distances are smaller, advance
    if (dist1 < CMToPx(wallModHelper.MAXCMDIST) && dist2 < CMToPx(wallModHelper.MAXCMDIST)) {
      // angles of shortest lines from source wall end/start pt to target wall must be similar
      var tarAngle = vectorRotationDegree(tarStart, tarEnd);
      var startAngle = vectorRotationDegree(startPt, tarStart);
      var endAngle = vectorRotationDegree(endPt, tarEnd);
      if (wallModHelper.angleSimilar(srcAngle, tarAngle) && wallModHelper.angleSimilar(startAngle, endAngle)) {
        // shortest lines must also be perpendicular to target wall
        var startSeg = uMath.segmentFromPoints(startPt, tarStart);
        var endSeg = uMath.segmentFromPoints(endPt, tarEnd);
        if (uMath.areSegmentsPerpendicular(srcSeg, startSeg, 14.9) && uMath.areSegmentsPerpendicular(srcSeg, endSeg, 14.9)) {
          results.push({ tarStart, tarEnd, dist: dist1 + dist2, tarAngle, roomId: sp.roomId, wallId: sp.wallId });
        }
      }
    }
  });

  if (!results.length) {
    return false;
  }

  // get closest

  results.sort(function (a, b) {
    return a.dist < b.dist ? -1 : 1;
  });

  // post checks

  var segIntersectTest = uMath.segmentFromPoints(uMath.pointsCenter(startPt, endPt), uMath.pointsCenter(results[0].tarStart, results[0].tarEnd));

  var spIntersectTest = wallModHelper.getAllStraightPaths(PlannerGlobals.rooms).filter(function (sp) {
    var wallIds = sp.points.map(function (p) { return p.id });
    return arrayIntersection(wallIds, [results[0].wallId, startPt.wallId]).length == 0;
  });
  var intersectsAny = spIntersectTest.some(function (sp) {
    for (var i = 0; i < sp.points.length - 1; i++) {
      var c = sp.points[i];
      var n = sp.points[i + 1];
      var seg = uMath.segmentFromPoints(c, n);
      if (line_line_intersect2(seg, segIntersectTest)) {
        return true;
      }
    }
    return false;
  });

  if (intersectsAny) {
    return false;
  }
  if (results[0]) {
    var startClosest = uDev.closestWallPtToPoint(results[0].tarStart)[0];
    var endClosest = uDev.closestWallPtToPoint(results[0].tarEnd)[0];
    results[0].tarStart = { x: startClosest.x, y: startClosest.y };
    results[0].tarEnd = { x: endClosest.x, y: endClosest.y };
    results[0].beginWallId = startClosest.wallId;
    results[0].endWallId = endClosest.wallId;
  }
  return results[0];
}

export const wallModHelper = {
  minLengthCM: {
    door: 40,
    window: 20
  },
  minLengthIN: {
    door: 16,
    window: 8
  },
  angleSimilar: function (angle1, angle2, threshold = 10) {
    angle1 = angle1 % 360;
    angle2 = angle2 % 360;
    var diff = Math.abs(angle2 - angle1);
    return diff < threshold || diff > 360 - threshold;
  },
  getAngleOfSection: function (pt1, pt2) {
    var slope = (pt2.y - pt1.y) / (pt2.x - pt1.x);
    var angle = Math.atan(slope);
    return radToDeg(angle);
  },
  /**
   *
   * @param {WallMod} wallModObj
   * @param {Wall} wall
   */
  wallModObjCompatibleWithWall: function (wallModObj, wall) {
    var wallPath = wallModHelper.roomStraightPaths(getRoomById(wall.roomId))
    .filter(function (path) {
      return path.indexOf(wall.id) != -1;
    })[0];

    return wallPath.indexOf(wallModObj.wallIds[0]) != -1;
  },
  roomStraightPathOfWall: function (roomId, wallId) {
    var room = getRoomById(roomId);
    var wall = getWallById(wallId);
    if (!room || !wall) { return false; }

    var straightPath = [wallId];
    var wallAngle = vectorRotationDegree(segPt(wall, 1), segPt(wall, 2));
    var orderIdx = wall.order_index;
    var nRoomWalls = room.poly.length;

    var startCheckIdx = (orderIdx + 1) % nRoomWalls;
    var endCheckIdx = orderIdx - 1 < 0 ? nRoomWalls + orderIdx - 1 : orderIdx - 1;
    var checkWallsRight = countIndexesIncl(startCheckIdx, endCheckIdx, nRoomWalls, false);
    var checkWallsLeft = countIndexesIncl(endCheckIdx, startCheckIdx, nRoomWalls, true);

    for (var oIdx of checkWallsRight) {
      var nextIdx = (oIdx + 1) % nRoomWalls;
      var pts = [room.poly[oIdx], room.poly[nextIdx]];
      var currWallAngle = vectorRotationDegree(pts[0], pts[1]);
      if (wallModHelper.angleSimilar(wallAngle, currWallAngle, 8)) {
        straightPath.push(room.poly[oIdx].id);
      }
      else {
        break;
      }
    }

    for (var oIdx of checkWallsLeft) {
      var nextIdx = (oIdx + 1) % nRoomWalls;
      var pts = [room.poly[oIdx], room.poly[nextIdx]];
      var currWallAngle = vectorRotationDegree(pts[0], pts[1]);
      if (wallModHelper.angleSimilar(wallAngle, currWallAngle, 8)) {
        straightPath.unshift(room.poly[oIdx].id);
      }
      else {
        break;
      }
    }

    return straightPath;

  },
  roomStraightPaths: function (room) {
    return wallModHelper.getAllStraightPaths([room]).map(function (path) {
      return path.points.slice(0, path.points.length - 1).map(function (pt) { return pt.id });
    });
  },
  /**
   *
   * @param {Room[]} rooms
   * @returns {StraightPath[]}
   */
  getAllStraightPaths: function (rooms) {
    var paths = [];
    rooms.forEach(function (room) {
      for (var i = 0; i < room.poly.length; i++) {
        var curr = room.poly[i];
        var next = room.poly[(i + 1) % room.poly.length];
        paths.push({
          roomId: room.id,
          points: [curr, next],
          path: uMath.segmentFromPoints(curr, next),
          angle: vectorRotationDegree(curr, next),
          wallId: curr.id
        });
      }
    });

    var pathsGrouped = {};
    paths.forEach(function (path) {
      if (!(path.roomId in pathsGrouped)) {
        pathsGrouped[path.roomId] = [];
      }
      pathsGrouped[path.roomId].push(path);
    });

    var maxAllowedAngle = 8;

    for (var roomId in pathsGrouped) {
      var room = pathsGrouped[roomId];
      var currAngleIdx = 0;
      var i = 1;
      while (i < room.length) {
        if (wallModHelper.angleSimilar(room[i - 1].angle, room[i].angle, maxAllowedAngle)) {
          room[currAngleIdx].points.push(room[i].points[1]);
          room[i].toDelete = true;
        }
        else {
          currAngleIdx = i;
        }
        i++;
      }

      var i = room.length - 1;
      while (wallModHelper.angleSimilar(room[0].angle, room[i].angle, maxAllowedAngle)) {
        room[0].points.unshift(room[i].points[0]);
        room[i].toDelete = true;
        i--;
      }
    }

    paths = paths.filter(function (path) {
      return !("toDelete" in path);
    });

    return paths;
  },
  // returns paths of walls that look straight
  getAllStraightPaths_SINGLEWALLS: function (rooms) {
    var paths = [];
    rooms.forEach(function (room) {
      for (var i = 0; i < room.poly.length; i++) {
        var curr = room.poly[i];
        var next = room.poly[(i + 1) % room.poly.length];
        paths.push({
          roomId: room.id,
          points: [curr, next]
        });
      }
    });
    return paths;
  },
  // returns every segment of a path
  getAllSegmentsOfPath: function (path) {
    var segments = [];
    for (var i = 1; i < path.points.length; i++) {
      var prev = path.points[i - 1];
      var curr = path.points[i];
      segments.push({
        x1: prev.x,
        y1: prev.y,
        x2: curr.x,
        y2: curr.y,
        roomId: path.roomId,
        wallId: prev.id
      });
    }
    return segments;
  },
  // converts rect to the 4 lines that make it
  rectToLines: function (rect) {
    var topleft = { x: rect.x1, y: rect.y1 };
    var bottomleft = { x: rect.x1, y: rect.y1 + rect.height };
    var topright = { x: rect.x1 + rect.width, y: rect.y1 };
    var bottomright = { x: rect.x2, y: rect.y2 };
    return [
      { x1: topleft.x, y1: topleft.y, x2: topright.x, y2: topright.y },               //top
      { x1: bottomleft.x, y1: bottomleft.y, x2: bottomright.x, y2: bottomright.y },   //bottom
      { x1: topleft.x, y1: topleft.y, x2: bottomleft.x, y2: bottomleft.y },           //left
      { x1: topright.x, y1: topright.y, x2: bottomright.x, y2: bottomright.y },       //right
    ]
  },
  // return
  getSegmentRectIntersections: function (pathSegment, rect) {
    var intersections = [];
    var rectLines = wallModHelper.rectToLines(rect);
    rectLines.forEach(function (rLine) {
      var intersect = line_line_intersect(pathSegment, rLine);
      if (intersect != 'nir') {
        intersections.push({ intersect, roomId: pathSegment.roomId, wallId: pathSegment.wallId });
      }
    });
    return intersections;
  },
  getPathRectIntersections: function (path, rect) {
    var intersections = [];
    var segs = wallModHelper.getAllSegmentsOfPath(path);
    segs.forEach(function (segment) {
      intersections = intersections.concat(wallModHelper.getSegmentRectIntersections(segment, rect));
    })
    return intersections;
  },
  pointsFromIntersection: function (intersection) {
    return intersection.wallIntersects.map(function (int) {
      return int.intersect;
    });
  },
  removeAllWallMods: function () {
    d3.selectAll('.doorOrWindow').remove();
  },
  deletingWallModId: {},
  
  setIsWallModConnAJAXWait(id, val) {
    var mod = PlannerGlobals.wallModConnections.find(function (wallmod) {
      return wallmod.id == id;
    });
    if (!mod) {
      return false;
    }
    mod.isAJAXWait = val;
    mod.rooms.forEach(function (room) {
      room.isAJAXWait = val;
    });
  },
  getIsWallModConnAJAXWait(id) {
    var mod = PlannerGlobals.wallModConnections.find(function (wallmod) {
      return wallmod.id == id;
    });
    if (!mod) {
      return false;
    }
    return !!mod.isAJAXWait;
  },
  
  
  polyLineFromIntersection(int) {
    var selWallIds = wallModHelper.selectionRealWallIds(int.roomId, int.wallIntersects[0].wallId, int.wallIntersects[int.wallIntersects.length - 1].wallId);
    if (selWallIds.length == 0) {
      return [];
    }
    return wallModHelper.polyLineFromSelection(int.wallIntersects[0].intersect, int.wallIntersects[int.wallIntersects.length - 1].intersect, selWallIds);
  },

  /**
   *
   * @param {WallMod} wallMod
   */
  polyLineFromWallMod(wallMod) {
    var selWallIds = wallModHelper.selectionRealWallIds(wallMod.roomId, wallMod.wallIds[0], wallMod.wallIds[wallMod.wallIds.length - 1]);
    if (selWallIds.length == 0) {
      return [];
    }
    return wallModHelper.polyLineFromSelection(wallMod.points[0], wallMod.points[1], selWallIds);
  },
  /**
   *
   * @param {WallMod} wallMod
   * @param {WallModConnection} wallModConn
   */
  drawLine: function (wallMod, wallModConn) {
    var selWallIds = wallModHelper.selectionRealWallIds(wallMod.roomId, wallMod.wallIds[0], wallMod.wallIds[wallMod.wallIds.length - 1]);
    if (selWallIds.length == 0) {
      return [];
    }
    var linePts = wallModHelper.polyLineFromSelection(wallMod.points[0], wallMod.points[1], selWallIds);

    var lineFunction = d3.line()
      .x(function (d) { return d.x; })
      .y(function (d) { return d.y; })
      .curve(d3.curveLinear);

    d3.select('.doorOrWindowContainer').append('path')
      .attr('d', lineFunction(linePts))
      .attr('fill', 'none')
      .attr('class', 'doorOrWindow')
      .attr('stroke', wallModConn.type == 'door' ? 'black' : '#3598dc')
      .attr('stroke-opacity', wallModConn.isAJAXWait ? 0.5 : 1)
      .attr('stroke-width', 1.5)
      .attr('data-wallModConnId', wallModConn.id)
      .attr('data-wallModId', wallMod.id);

  },
  distanceFromPoints: function (pt1, pt2) {
    return Math.sqrt(Math.pow(pt2.x - pt1.x, 2) + Math.pow(pt2.y - pt1.y, 2));
  },
  getWallModCenter: function (wall) {
    return { x: (wall[0].x + wall[1].x) / 2, y: (wall[0].y + wall[1].y) / 2 };
  },
  distanceOfWalls: function (info) {
    var p1 = info.rooms[0].points;
    var p2 = info.rooms[1].points;
    var r1Center = wallModHelper.getWallModCenter(p1);
    var r2Center = wallModHelper.getWallModCenter(p2);
    return wallModHelper.distanceFromPoints(r1Center, r2Center);
  },

  /**
   *
   * @param {Point} beginPt
   * @param {Point} endPt
   * @param {number[]} wallIds
   */
  polyLineFromSelection: function (beginPt, endPt, wallIds) {
    var pts = [];
    // add begin
    pts.push(beginPt);

    if (wallIds.length > 1) {
      // add connection points btwn walls
      for (var i = 1; i < wallIds.length; i++) {
        var curr = getWallById(wallIds[i]);
        var prev = getWallById(wallIds[i - 1]);
        var toAdd = 0;
        // fix if passing through the first wall (with idx 0)
        if (curr.order_index == 0 && prev.order_index != 1 || prev.order_index == 0 && curr.order_index != 1) {
          toAdd = 1;
        }
        if (curr.order_index > prev.order_index) {
          pts.push(segPt(curr, 1 + toAdd));
        }
        else {
          pts.push(segPt(prev, 1 + toAdd));
        }
      }
    }

    // add end
    pts.push(endPt);

    return pts;
  },

  selectionRealWallIds: function (roomId, beginWallId, endWallId) {
    var wallIds = [];
    var inRoom = getRoomById(roomId);
    var begin = getWallById(beginWallId);
    var end = getWallById(endWallId);
    var walls = wallsOfRoom(inRoom);
    var sps = wallModHelper.getAllStraightPaths([inRoom]);
    var sp = sps.find(function (sp) {
      return sp.points.some(function (p) {
            return p.id == begin.id;
          }) &&
          sp.points.some(function (p) {
            return p.id == end.id;
          });
    });
    if (!sp) {
      return [];
    }
    var beginPathIdx = sp.points.findIndex(function (p) { return p.id == begin.id });
    var endPathIdx = sp.points.findIndex(function (p) { return p.id == end.id });
    var count = countIndexesIncl(begin.order_index, end.order_index, walls.length, beginPathIdx > endPathIdx);
    wallIds = count.map(function (orderidx) {
      return walls[orderidx].id;
    });

    return wallIds;
  },
  wallIdsFromIntersection: function (intersection) {
    var wallIds = [];
    var inRoom = getRoomById(intersection.roomId);
    var begin = getWallById(intersection.wallIntersects[0].wallId);
    var end = getWallById(intersection.wallIntersects[1].wallId);
    var walls = wallsOfRoom(inRoom);
    var sps = wallModHelper.getAllStraightPaths([inRoom]);
    var sp = sps.find(function (sp) {
      return sp.points.some(function (p) {
            return p.id == begin.id;
          }) &&
          sp.points.some(function (p) {
            return p.id == end.id;
          });
    });
    var beginPathIdx = sp.points.findIndex(function (p) { return p.id == begin.id });
    var endPathIdx = sp.points.findIndex(function (p) { return p.id == end.id });
    var count = countIndexesIncl(begin.order_index, end.order_index, walls.length, beginPathIdx > endPathIdx);
    wallIds = count.map(function (orderidx) {
      return walls[orderidx].id;
    });

    return wallIds;
  },
  rectContainsEntity: function (rect) {
    var rect2 = { x: rect.x1, y: rect.y1, width: rect.width, height: rect.height };
    for (var wall of wallModHelper.getWallMods()) {
      for (var point of wall.points) {
        if (uMath.rectContainsPoint(rect2, point)) {
          return true;
        }
      }
    }
    return false;
  },
  rectIntersectsEntity: function (rect) {
    var rect2 = { x: rect.x1, y: rect.y1, width: rect.width, height: rect.height };
    for (var wall of wallModHelper.getWallMods()) {
      if (uMath.rectIntersectsSegment(rect2, uMath.segmentFromPoints(wall.points[0], wall.points[1]))) {
        return true;
      }
    }
    return false;
  },
  segmentFromWallMod: function (wallMod) {
    return uMath.segmentFromPoints(wallMod.points[0], wallMod.points[1]);
  },
  /**
   *
   * @param {WallModConnection} wallModConnection
   */
  wallModConnPointPairs(wallModConnection) {
    var points0 = wallModConnection.rooms[0].points;
    var points1 = wallModConnection.rooms[1].points;
    var pair1, pair2;

    if (uMath.pointDistance(points0[0], points1[0]) < uMath.pointDistance(points0[0], points1[1])) {
      pair1 = [points0[0], points1[0]];
      pair2 = [points0[1], points1[1]];
    }
    else {
      pair1 = [points0[0], points1[1]];
      pair2 = [points0[1], points1[0]];
    }
    return { pair1, pair2 };
  },
  
  
  MAXCMDIST: 50,
  /**
   *
   * @param {WallModIntersection[]} intersections
   */
  intersectionsOverlap: function (intersections) {
    var thisVar = this;
    var newSegments = intersections
    .map(function (int) {
      return thisVar.polyLineFromIntersection(int);
    })
    .reduce(function (p, c) { if (c) { return p.concat(uMath.polyLineToSegments(c)); } else { return p; } }, []);

    var relevantWallIds = intersections.map(function (int) {
      return thisVar.selectionRealWallIds(int.roomId, int.wallIntersects[0].wallId, int.wallIntersects[int.wallIntersects.length - 1].wallId);
    })
    .reduce(function (p, c) {
      if (c) {
        return p.concat(c);
      }
      else {
        return p;
      }
    }, []);

    var wallModSegments = wallModHelper.getWallMods()
    .filter(function (wm) {
      return arrayIntersection(wm.wallIds, relevantWallIds).length > 0;
    })
    .map(function (wm) {
      return thisVar.polyLineFromWallMod(wm);
    })
    .reduce(function (p, c) {
      if (c) {
        return p.concat(uMath.polyLineToSegments(c));
      } else {
        return p;
      }
    }, []);

    for (var i = 0; i < newSegments.length; i++) {
      for (var j = 0; j < wallModSegments.length; j++) {
        if (uMath.segmentsOverlap(newSegments[i], wallModSegments[j])) {
          return true;
        }
      }
    }

    return false;
  },
  
  /**
   *
   * @param {Point} point
   * @param {WallMod[]} wallMods
   */
  isPointOnWallMod: function (point, wallMods, threshold = 1) {
    return wallMods.some(function (wallMod) {
      var closestPt = segmentClosestPointToPoint(point, wallMod.points[0], wallMod.points[1]);
      var dist = uMath.pointDistance(point, closestPt);
      return dist < threshold;
    });
  },
  /**
   * @returns {WallMod[]}
   */
  getWallMods: function () {
    var returnedMods = [];

    for (var i = 0; i < PlannerGlobals.wallModConnections.length; i++) {
      var clonedObj = JSON.parse(JSON.stringify(PlannerGlobals.wallModConnections[i]));
      if (clonedObj.type) {
        for (var j = 0; j < clonedObj.rooms.length; j++) {
          clonedObj.rooms[j].connId = clonedObj.id;
          clonedObj.rooms[j].type = clonedObj.type;
          clonedObj.rooms[j].isSingle = clonedObj.rooms.length == 1;
          clonedObj.rooms[j].pair = clonedObj.rooms[+!j];
          returnedMods.push(clonedObj.rooms[j]);
        }
      }
    }

    return returnedMods;

  },
  getWallModsOfRoom: function (roomId) {
    return this.getWallMods().filter(function (mod) {
      return mod.roomId == roomId;
    });
  },
  /**
   *
   * @param {number} roomId
   * @param {"door" | "window"} type
   */
  getWallModsOfRoomByType: function (roomId, type) {
    return this.getWallMods().filter(function (mod) {
      return mod.roomId == roomId && mod.type == type;
    });
  },
  /**
   *
   * @param {"door" | "window"} type
   */
  getWallModsByType: function (type) {
    return this.getWallMods().filter(function (mod) {
      return mod.type == type;
    });
  },
  /**
   *
   * @param {number[]} wallIds
   */
  getWallModsOfWall: function (wallIds) {
    return this.getWallMods().filter(function (mod) {
      return arrayIntersection(wallIds, mod.wallIds).length > 0;
    });
  },
  getWallModsByConnId: function (connId, roomId = null) {
    return this.getWallMods()
    .filter(function (mod) {
      return mod.connId == connId && (!roomId || roomId == mod.roomId);
    });
  },
  getWallModConnOtherRoom: function (connId, roomId) {
    var conn = PlannerGlobals.wallModConnections.find(function (conn) {
      return conn.id == connId;
    });

    var thatRoomIdx = conn.rooms.findIndex(function (room) {
      return room.roomId == roomId;
    });

    if (thatRoomIdx == -1 || conn.rooms.length != 2) {
      return false;
    }
    else {
      return conn.rooms[+!thatRoomIdx];
    }
  },
  getWallModById: function (id) {
    for (var wallMod of this.getWallMods()) {
      if (wallMod.id == id) {
        return wallMod;
      }
    }
  },
  getWallModConnById: function (connId) {
    return PlannerGlobals.wallModConnections.find(function (conn) {
      return +conn.id === +connId;
    });
  },
  /**
   *
   * @param {number[]} outerWallIds
   * @param {any[]} outerBorder
   * @returns {WallMod[]}
   */
  getOuterWallMods: function (outerWallIds, outerBorder) {
    return wallModHelper.getWallModsOfWall(outerWallIds).filter(function (wallMod) {
      var pt0 = uMath.pointClosestPtToPolygon(wallMod.points[0], outerBorder);
      var pt1 = uMath.pointClosestPtToPolygon(wallMod.points[1], outerBorder);

      var siblingRoom = wallMod.pair ? getRoomById(wallMod.pair.roomId) : null;
      var isSiblingInside = siblingRoom && !isRoomOutside(siblingRoom, outerBorder);

      if (isSiblingInside) {
        return false;
      }

      return uMath.areSegmentsPerpendicular(pt0.segment, uMath.segmentFromPoints(pt0, wallMod.points[0])) &&
          uMath.areSegmentsPerpendicular(pt1.segment, uMath.segmentFromPoints(pt1, wallMod.points[1]));
    });
  },
  getWallModFurthestPtIdxByWallModId(wmid) {
    var wallMod = this.getWallModById(wmid);
    var room = getRoomById(wallMod.roomId);
    return this.getWallModFurthestPtIdx(wallMod, room);
  },
  /**
   *
   * @param {WallMod} wallMod
   * @param {Room} room
   */
  getWallModFurthestPtIdx: function (wallMod, room) {
    var straightPaths = wallModHelper.roomStraightPaths(room);
    var relevantStraightPath = straightPaths.find(function (path) {
      return path.indexOf(+wallMod.wallIds[0]) !== -1;
    });
    if (!relevantStraightPath) {
      return null;
    }

    var walls = wallsOfRoom(room);
    var firstWall = walls.find(function (wall) { return +wall.id === relevantStraightPath[0]; });
    var lastWall = walls.find(function (wall) { return +wall.id === relevantStraightPath[relevantStraightPath.length - 1]; });

    if (!firstWall || !lastWall) {
      return null;
    }

    var firstDist, secondDist;

    // comparing how the wallMod is laid out on the wall
    if (
        uMath.pointDistance(wallMod.points[0], uMath.nThSegmentPoint(firstWall, 1)) <
        uMath.pointDistance(wallMod.points[1], uMath.nThSegmentPoint(firstWall, 1))
    ) {
      firstDist = uMath.pointDistance(wallMod.points[0], uMath.nThSegmentPoint(firstWall, 1));
      secondDist = uMath.pointDistance(wallMod.points[1], uMath.nThSegmentPoint(lastWall, 2));
      return firstDist > secondDist ? 0 : 1;
    }
    else {
      firstDist = uMath.pointDistance(wallMod.points[1], uMath.nThSegmentPoint(firstWall, 1));
      secondDist = uMath.pointDistance(wallMod.points[0], uMath.nThSegmentPoint(lastWall, 2));
      return firstDist > secondDist ? 1 : 0;
    }

  }
}
